// src/firebase/cohorts.js
// Cohorts Service - manages student cohorts and registration codes

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getSubmissionModes } from './settings';
import { getCurrentUserOrgId } from './orgs';
import { isLinkedStudent, studentViewForOrg, withLinkedStudents, applyStudentChanges, removeLinkedStudentFromOrg } from './tutorLinks';

/**
 * Generate a cohort code from name
 * E.g., "March 2026" -> "MARCH2026"
 *
 * Codes only need to be unique within one tutor's own cohorts (see
 * createCohort's uniqueness check and getCohortByCode below, both scoped by
 * orgId) - two tutors can freely use the same code/name for their own
 * intakes, e.g. both "JAN2026".
 */
export const generateCohortCode = (name) => {
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '') // Remove special chars
    .replace(/\s+/g, ''); // Remove spaces
};

/**
 * Create a new cohort.
 * orgId defaults to the signed-in caller's own org (resolved via
 * getCurrentUserOrgId()) - callers with a known org context from elsewhere
 * should pass it explicitly instead. Without this, every cohort created
 * through the app would be stamped with DEFAULT_ORG_ID regardless of who
 * created it, which the create rule then correctly rejects for any org
 * other than Aileen's ("missing or insufficient permissions").
 */
export const createCohort = async (cohortData) => {
  try {
    const { name, code, startDate, endDate, orgId = await getCurrentUserOrgId() } = cohortData;

    // Check if code already exists WITHIN THIS ORG ONLY - two tutors can
    // freely reuse the same code for their own cohorts (see
    // generateCohortCode's comment and getCohortByCode below).
    const existingQuery = query(
      collection(db, 'cohorts'),
      where('orgId', '==', orgId),
      where('code', '==', code)
    );
    const existingSnapshot = await getDocs(existingQuery);

    // A soft-deleted cohort (see softDeleteCohort) doesn't block reusing its
    // code - no reason to make a tutor wait out the recovery window just to
    // reuse a code they've already deleted.
    if (existingSnapshot.docs.some(d => !d.data().pendingDeletion)) {
      throw new Error('A cohort with this code already exists');
    }

    const docRef = await addDoc(collection(db, 'cohorts'), {
      name,
      code,
      startDate,
      endDate,
      orgId,
      active: true,
      createdAt: serverTimestamp(),
      studentCount: 0
    });
    
    console.log('Cohort created:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Create cohort error:', error);
    throw error;
  }
};

/**
 * Get all cohorts.
 * Scoped to the caller's own org by default - pass { allOrgs: true } only
 * from code already gated to platform admins (PlatformAdminView), which is
 * the one legitimate cross-org caller. Without this, every other caller
 * (Cohort Management, Deadlines, Head Marker Dashboard) would see every
 * org's cohort names, since cohorts/{cohortId}'s read rule is public
 * (`allow read: if true`, needed for unauthenticated registration lookups)
 * and doesn't itself scope list queries by org.
 */
export const getAllCohorts = async ({ allOrgs = false } = {}) => {
  try {
    const conditions = [];
    if (!allOrgs) {
      const orgId = await getCurrentUserOrgId();
      conditions.push(where('orgId', '==', orgId));
    }
    const q = query(
      collection(db, 'cohorts'),
      ...conditions,
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const cohorts = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      // Filtered client-side rather than with a where() clause - cohort
      // counts per org are small, and this avoids yet another composite
      // index for what's a rarely-populated boolean. Excludes soft-deleted
      // cohorts from every normal listing - see getRecentlyDeletedCohorts.
      if (data.pendingDeletion) return;
      cohorts.push({
        id: doc.id,
        ...data
      });
    });

    return cohorts;
  } catch (error) {
    console.error('Get cohorts error:', error);
    throw error;
  }
};

/**
 * Get this org's soft-deleted cohorts (pendingDeletion === true), most
 * recently deleted first - for the "Recently Deleted" recovery view.
 */
export const getRecentlyDeletedCohorts = async () => {
  try {
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'cohorts'),
      where('orgId', '==', orgId)
    );
    const snapshot = await getDocs(q);
    const cohorts = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.pendingDeletion) cohorts.push({ id: doc.id, ...data });
    });
    cohorts.sort((a, b) => {
      const aTime = a.pendingDeletionAt?.toDate?.() || new Date(0);
      const bTime = b.pendingDeletionAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    });
    return cohorts;
  } catch (error) {
    console.error('Get recently deleted cohorts error:', error);
    throw error;
  }
};

/**
 * Get cohort by code - checked server-side by the lookupCohortByCode
 * function (2026-09-28). Cohorts are no longer publicly readable, since that
 * let anyone list every tutor's cohorts and codes; this works before the
 * student has an account (registration), exactly as before.
 * orgId (from a ?org=<orgId> registration link) scopes the lookup to one
 * tutor - codes are only unique within one tutor. Returns
 * { id, name, code, orgId, active }, { ambiguous: true } when a code typed
 * without a tutor matches more than one, or null.
 */
export const getCohortByCode = async (code, orgId) => {
  try {
    const lookup = httpsCallable(getFunctions(getApp()), 'lookupCohortByCode');
    const result = await lookup({ code, orgId: orgId || null });
    return result.data.cohort || null;
  } catch (error) {
    console.error('Get cohort by code error:', error);
    throw error;
  }
};

/**
 * Update cohort
 */
export const updateCohort = async (cohortId, updates) => {
  try {
    const docRef = doc(db, 'cohorts', cohortId);
    await updateDoc(docRef, updates);
    console.log('Cohort updated:', cohortId);
  } catch (error) {
    console.error('Update cohort error:', error);
    throw error;
  }
};

// How long a soft-deleted cohort stays recoverable before
// sweepPendingCohortDeletions (functions/index.js) permanently removes it -
// must match COHORT_DELETION_RETENTION_DAYS there.
export const COHORT_DELETION_RETENTION_DAYS = 90;

/**
 * Soft-delete a cohort: marks it pendingDeletion rather than touching any
 * actual data. Submissions, PDFs, marking data, and student profiles are
 * left completely untouched - only sweepPendingCohortDeletions (after the
 * retention window) or permanentlyDeleteCohortNow below ever actually
 * deletes them, both server-side via the Admin SDK. This makes an accidental
 * delete trivially undoable via restoreCohort.
 */
export const softDeleteCohort = async (cohortId) => {
  try {
    await updateDoc(doc(db, 'cohorts', cohortId), {
      pendingDeletion: true,
      pendingDeletionAt: serverTimestamp()
    });
    console.log('Cohort marked for deletion:', cohortId);
  } catch (error) {
    console.error('Soft-delete cohort error:', error);
    throw error;
  }
};

/**
 * Undo a soft-delete - the cohort returns to normal immediately, since
 * nothing underneath it was ever touched.
 */
export const restoreCohort = async (cohortId) => {
  try {
    await updateDoc(doc(db, 'cohorts', cohortId), {
      pendingDeletion: false,
      pendingDeletionAt: null
    });
    console.log('Cohort restored:', cohortId);
  } catch (error) {
    console.error('Restore cohort error:', error);
    throw error;
  }
};

/**
 * Permanently delete a soft-deleted cohort right now, instead of waiting
 * out the retention window. Runs server-side (Admin SDK, via the
 * permanentlyDeleteCohort Cloud Function) - the client never directly
 * cascade-deletes submissions/Storage files/student profiles any more.
 */
export const permanentlyDeleteCohortNow = async (cohortId) => {
  try {
    const functions = getFunctions(getApp());
    const permanentlyDeleteCohortFn = httpsCallable(functions, 'permanentlyDeleteCohort');
    await permanentlyDeleteCohortFn({ cohortId });
    console.log('Cohort permanently deleted:', cohortId);
  } catch (error) {
    console.error('Permanently delete cohort error:', error);
    throw new Error(error.message);
  }
};

/**
 * Increment student count for cohort
 */
export const incrementCohortStudentCount = async (cohortId) => {
  try {
    const docRef = doc(db, 'cohorts', cohortId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const currentCount = docSnap.data().studentCount || 0;
      await updateDoc(docRef, {
        studentCount: currentCount + 1
      });
    }
  } catch (error) {
    console.error('Increment student count error:', error);
    // Don't throw - this is non-critical
  }
};

const isRecentUnreviewed = (since) => (s) => {
  // A student waiting for approval always shows, however long ago they registered
  if (s.approvalPending === true) return true;
  const createdAt = s.createdAt?.toDate?.();
  return !!createdAt && createdAt >= since && !s.reviewed;
};

/**
 * Get recent registrations (last 30 days, unreviewed only)
 * Simplified to avoid composite index requirements
 */
export const getRecentRegistrations = async () => {
  try {
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'users'),
      where('orgId', '==', orgId),
      where('role', '==', 'student'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const registrations = [];
    
    // Filter for last 30 days and unreviewed in code
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate();
      
      // Only include if created in last 30 days and not reviewed (or waiting for approval)
      if (data.pendingDeletion) return; // in Recently Deleted
      if ((createdAt && createdAt >= thirtyDaysAgo && !data.reviewed) || data.approvalPending === true) {
        registrations.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    // Linked tutors: include students who joined this org as a second tutor
    const merged = (await withLinkedStudents(registrations, isRecentUnreviewed(thirtyDaysAgo)))
      .filter(isRecentUnreviewed(thirtyDaysAgo))
      .sort((a, b) => (b.createdAt?.toDate() || new Date(0)) - (a.createdAt?.toDate() || new Date(0)));

    // Limit to 50 most recent
    return merged.slice(0, 50);
  } catch (error) {
    console.error('Get recent registrations error:', error);
    
    // If index error, fall back to simpler query
    if (error.code === 'failed-precondition') {
      console.log('Falling back to simple query...');
      try {
        const orgId = await getCurrentUserOrgId();
        const simpleQuery = query(
          collection(db, 'users'),
          where('orgId', '==', orgId),
          where('role', '==', 'student')
        );
        
        const snapshot = await getDocs(simpleQuery);
        const registrations = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Only include unreviewed
          if (!data.reviewed && !data.pendingDeletion) {
            registrations.push({
              id: doc.id,
              ...data
            });
          }
        });
        
        // Sort by createdAt in code
        registrations.sort((a, b) => {
          const aDate = a.createdAt?.toDate() || new Date(0);
          const bDate = b.createdAt?.toDate() || new Date(0);
          return bDate - aDate;
        });
        
        const merged = await withLinkedStudents(registrations, s => !s.reviewed);
        merged.sort((a, b) => (b.createdAt?.toDate() || new Date(0)) - (a.createdAt?.toDate() || new Date(0)));
        return merged.filter(s => !s.reviewed).slice(0, 50);
      } catch (fallbackError) {
        console.error('Fallback query error:', fallbackError);
        return [];
      }
    }
    
    return [];
  }
};

/**
 * Approve a student registration (marks as reviewed)
 */
export const approveRegistration = async (studentId) => {
  try {
    // Also lifts tutor approval (approvalPending) - the Approve button is
    // what lets a held student in
    await applyStudentChanges(studentId, (ts) => ({ reviewed: true, reviewedAt: ts, approvalPending: false }));
    console.log('Registration approved:', studentId);
  } catch (error) {
    console.error('Approve registration error:', error);
    throw error;
  }
};

/**
 * Delete a student account (removes from Firestore and Auth)
 */
export const STUDENT_DELETION_RETENTION_DAYS = 30;

// Moves the student to Recently Deleted (locked out, kept for
// STUDENT_DELETION_RETENTION_DAYS, restorable) - or, with
// { permanent: true }, deletes them and all their work immediately.
export const deleteStudentAccount = async (studentId, studentEmail, { permanent = false } = {}) => {
  try {
    // Linked tutors: a student also linked to another tutor keeps their
    // account - just unlink them from this org.
    const existing = await getDoc(doc(db, 'users', studentId));
    if (existing.exists() && isLinkedStudent(existing.data())) {
      await removeLinkedStudentFromOrg(studentId);
      console.log('Linked student removed from this org:', studentId);
      return;
    }

    // All server-side (see deleteUserAccount in functions/index.js)
    const functions = getFunctions(getApp());
    const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
    const result = await deleteUserAccount({ userId: studentId, permanent });
    console.log('Student account deleted:', studentId, result.data);
  } catch (error) {
    console.error('Delete student account error:', error);
    throw error;
  }
};

/**
 * This org's students in Recently Deleted (see deleteStudentAccount),
 * most recently deleted first
 */
export const getRecentlyDeletedStudents = async () => {
  const orgId = await getCurrentUserOrgId();
  const snapshot = await getDocs(query(
    collection(db, 'users'),
    where('orgId', '==', orgId),
    where('pendingDeletion', '==', true)
  ));
  return snapshot.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.pendingDeletionAt?.toMillis?.() || 0) - (a.pendingDeletionAt?.toMillis?.() || 0));
};

/**
 * Undo a student deletion during the retention window. Returns
 * { cohortRestored, cohortName } - cohortRestored is false if the cohort
 * they were in has since been deleted, leaving them without one.
 */
export const restoreDeletedStudent = async (studentId) => {
  const functions = getFunctions(getApp());
  const result = await httpsCallable(functions, 'restoreDeletedStudent')({ userId: studentId });
  return result.data;
};

/**
 * Live number of students in each of this org's cohorts - { [cohortId]: n },
 * including linked students, excluding Recently Deleted. Replaces the
 * stored cohorts/{id}.studentCount, which only ever went up.
 */
export const getCohortStudentCounts = async () => {
  const orgId = await getCurrentUserOrgId();
  const snapshot = await getDocs(query(
    collection(db, 'users'),
    where('orgId', '==', orgId),
    where('role', '==', 'student')
  ));
  const students = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  const merged = await withLinkedStudents(students, s => !!s.cohortId);
  const counts = {};
  merged.forEach(s => {
    if (s.cohortId && !s.pendingDeletion) counts[s.cohortId] = (counts[s.cohortId] || 0) + 1;
  });
  return counts;
};

/**
 * Whether a student also studies with another tutor (linked tutors) -
 * deleting them then only removes them from this org, immediately
 */
export const isStudentLinkedToOtherTutors = async (studentId) => {
  const snap = await getDoc(doc(db, 'users', studentId));
  return snap.exists() && isLinkedStudent(snap.data());
};

/**
 * Get all students in a cohort
 */
export const getCohortStudents = async (cohortId) => {
  try {
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'users'),
      where('orgId', '==', orgId),
      where('cohortId', '==', cohortId),
      where('role', '==', 'student')
    );
    
    const snapshot = await getDocs(q);
    const students = [];
    
    snapshot.forEach((doc) => {
      students.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Linked tutors: plus students linked to this cohort while another tutor is active
    const merged = (await withLinkedStudents(students, s => s.cohortId === cohortId)).filter(s => s.cohortId === cohortId);

    // Sort alphabetically by name
    merged.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return merged;
  } catch (error) {
    console.error('Get cohort students error:', error);
    throw error;
  }
};

/**
 * Update a student's display name (used to correct sync mismatches)
 */
export const updateStudentName = async (studentId, newName) => {
  try {
    await applyStudentChanges(studentId, () => ({ name: newName }));
  } catch (error) {
    console.error('Update student name error:', error);
    throw error;
  }
};

/**
 * Get all marked submissions for a student
 */
export const getStudentMarkedSubmissions = async (studentId) => {
  try {
    const { getStudentSubmissions } = await import('./submissions');
    const allSubmissions = await getStudentSubmissions(studentId);
    
    // Filter for only marked submissions
    const markedSubmissions = allSubmissions.filter(sub => sub.status === 'marked');
    
    // Sort by homework type
    markedSubmissions.sort((a, b) => a.homeworkType.localeCompare(b.homeworkType));
    
    return markedSubmissions;
  } catch (error) {
    console.error('Get student marked submissions error:', error);
    throw error;
  }
};

/**
 * Suspend a student account (they can view but not submit)
 */
export const suspendStudentAccount = async (studentId, reason = '') => {
  try {
    await applyStudentChanges(studentId, (ts) => ({
      suspended: true,
      suspendedAt: ts,
      suspendedBy: 'head_marker', // Could pass current user ID
      suspensionReason: reason
    }));
    console.log('Student account suspended:', studentId);
  } catch (error) {
    console.error('Suspend student account error:', error);
    throw error;
  }
};

/**
 * Reactivate a suspended student account
 */
export const reactivateStudentAccount = async (studentId) => {
  try {
    await applyStudentChanges(studentId, (ts) => ({
      suspended: false,
      reactivatedAt: ts,
      reactivatedBy: 'head_marker'
    }));
    console.log('Student account reactivated:', studentId);
  } catch (error) {
    console.error('Reactivate student account error:', error);
    throw error;
  }
};

/**
 * Set an extra-time accommodation for a student, applied to every exam-mode
 * homework they take (not per-homework-type - matches how a real
 * accommodation is usually a single agreed arrangement). One type at a time,
 * not stacked. Read/applied server-side in createOrResumeExamSession.
 */
export const setStudentExtraTime = async (studentId, { type, value }) => {
  try {
    await applyStudentChanges(studentId, (ts) => ({
      examExtraTime: { type, value, setAt: ts, setBy: 'head_marker' }
    }));
    console.log('Extra time set for student:', studentId, type, value);
  } catch (error) {
    console.error('Set student extra time error:', error);
    throw error;
  }
};

/**
 * Clear a student's extra-time accommodation
 */
export const clearStudentExtraTime = async (studentId) => {
  try {
    await applyStudentChanges(studentId, () => ({ examExtraTime: null }));
    console.log('Extra time cleared for student:', studentId);
  } catch (error) {
    console.error('Clear student extra time error:', error);
    throw error;
  }
};

/**
 * Move student to a different cohort
 */
export const moveStudentToCohort = async (studentId, newCohortId) => {
  try {
    // Get the new cohort details
    const cohortRef = doc(db, 'cohorts', newCohortId);
    const cohortSnap = await getDoc(cohortRef);
    
    if (!cohortSnap.exists()) {
      throw new Error('Target cohort not found');
    }
    
    const cohortData = cohortSnap.data();
    
    // Get student's current cohort
    const studentRef = doc(db, 'users', studentId);
    const studentSnap = await getDoc(studentRef);
    // Linked tutors: the student's cohort WITH THIS TUTOR, not whichever
    // tutor they currently have active
    const oldCohortId = studentViewForOrg(studentSnap.data(), await getCurrentUserOrgId())?.cohortId;

    // Update student's cohort
    await applyStudentChanges(studentId, (ts) => ({
      cohortId: newCohortId,
      cohortName: cohortData.name,
      movedAt: ts,
      movedBy: 'head_marker',
      previousCohortId: oldCohortId
    }));
    
    // Update old cohort student count
    if (oldCohortId) {
      const oldCohortRef = doc(db, 'cohorts', oldCohortId);
      const oldCohortSnap = await getDoc(oldCohortRef);
      if (oldCohortSnap.exists()) {
        const currentCount = oldCohortSnap.data().studentCount || 0;
        await updateDoc(oldCohortRef, {
          studentCount: Math.max(0, currentCount - 1)
        });
      }
    }
    
    // Update new cohort student count
    await incrementCohortStudentCount(newCohortId);
    
    console.log(`Student ${studentId} moved from ${oldCohortId} to ${newCohortId}`);
  } catch (error) {
    console.error('Move student to cohort error:', error);
    throw error;
  }
};

/**
 * The homework types a student has handed in for a cohort (same "handed in"
 * test as hasSubmittedHomework in functions/index.js), for the move dialog
 */
export const getStudentHomeworkInCohort = async (studentId, cohortId) => {
  const { getStudentSubmissions } = await import('./submissions');
  const submissions = await getStudentSubmissions(studentId);
  return [...new Set(submissions
    .filter(s => s.cohortId === cohortId && !s.deletedByStudent &&
      ((Array.isArray(s.originalFilePaths) && s.originalFilePaths.length > 0) ||
        (s.fileCount || 0) > 0 || !!s.originalFileUrl))
    .map(s => s.homeworkType))];
};

/**
 * After moveStudentToCohort: brings the chosen homework into the new cohort,
 * so the student can't do it again. Returns { moved, skipped } - skipped ones
 * were already handed in over there, so that copy was left alone.
 */
export const moveHomeworkToCohort = async (studentId, fromCohortId, toCohortId, homeworkTypes) => {
  const result = await httpsCallable(getFunctions(getApp()), 'moveStudentHomeworkToCohort')({ studentId, fromCohortId, toCohortId, homeworkTypes });
  return result.data;
};

/**
 * Get all suspended students
 */
export const getSuspendedStudents = async () => {
  try {
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'users'),
      where('orgId', '==', orgId),
      where('role', '==', 'student'),
      where('suspended', '==', true)
    );
    
    const snapshot = await getDocs(q);
    const students = [];
    
    snapshot.forEach((doc) => {
      if (doc.data().pendingDeletion) return; // in Recently Deleted
      students.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Linked tutors: suspension is per tutor - use this org's view throughout
    const merged = (await withLinkedStudents(students, s => s.suspended === true)).filter(s => s.suspended === true);
    students.length = 0;
    students.push(...merged);

    // Sort by suspension date (most recent first)
    students.sort((a, b) => {
      const aDate = a.suspendedAt?.toDate() || new Date(0);
      const bDate = b.suspendedAt?.toDate() || new Date(0);
      return bDate - aDate;
    });
    
    return students;
  } catch (error) {
    console.error('Get suspended students error:', error);
    throw error;
  }
};

/**
 * Archive student data (prepare for export)
 * Returns data ready to be saved/downloaded
 */
export const archiveStudentData = async (studentId) => {
  try {
    // Get student details
    const studentRef = doc(db, 'users', studentId);
    const studentSnap = await getDoc(studentRef);
    
    if (!studentSnap.exists()) {
      throw new Error('Student not found');
    }
    
    // Linked tutors: this tutor's cohort details
    const studentData = studentViewForOrg(studentSnap.data(), await getCurrentUserOrgId());
    
    // Get all student submissions
    const { getStudentSubmissions } = await import('./submissions');
    const submissions = await getStudentSubmissions(studentId);
    
    // Prepare archive data
    const archiveData = {
      student: {
        id: studentId,
        name: studentData.name,
        email: studentData.email,
        phone: studentData.phone,
        cohortName: studentData.cohortName,
        cohortId: studentData.cohortId,
        createdAt: studentData.createdAt?.toDate().toISOString(),
        archivedAt: new Date().toISOString()
      },
      submissions: submissions.map(sub => ({
        homeworkType: sub.homeworkType,
        status: sub.status,
        submittedAt: sub.submittedAt?.toDate().toISOString(),
        markedAt: sub.markedAt?.toDate().toISOString(),
        markerName: sub.markerName,
        mark: sub.mark,
        maxMark: sub.maxMark,
        feedback: sub.feedback || '',
        fileUrls: sub.originalFileUrls || [sub.originalFileUrl],
        fileNames: sub.fileNames || ['Submitted File'],
        markedFileUrl: sub.markedFileUrl
      })),
      summary: {
        totalSubmissions: submissions.length,
        markedSubmissions: submissions.filter(s => s.status === 'marked').length,
        pendingSubmissions: submissions.filter(s => s.status === 'pending').length,
        beingMarkedSubmissions: submissions.filter(s => s.status === 'being_marked').length
      }
    };
    
    return archiveData;
  } catch (error) {
    console.error('Archive student data error:', error);
    throw error;
  }
};

/**
 * Delete student account with archive option
 * Enhanced version that checks for suspension requirement
 */
export const deleteStudentAccountEnhanced = async (studentId, studentEmail, archived = false) => {
  try {
    if (!archived) {
      throw new Error('Student data must be archived before deletion. Please archive first.');
    }
    
    await deleteStudentAccount(studentId, studentEmail);
  } catch (error) {
    console.error('Delete student account error:', error);
    throw error;
  }
};

/**
 * Get this cohort's per-homework-type submission mode override, if any.
 * Stored as a sibling field on the same doc as deadlines (e.g. TH1_modeOverride),
 * so it rides along on every existing read of that doc for free.
 * Returns null if no override is set (caller should fall back to the global default).
 */
export const getSubmissionModeOverride = async (cohortId, homeworkType) => {
  try {
    const docSnap = await getDoc(doc(db, 'cohorts', cohortId, 'deadlines', 'current'));
    if (!docSnap.exists()) return null;
    return docSnap.data()[`${homeworkType}_modeOverride`] || null;
  } catch (error) {
    console.error('Get submission mode override error:', error);
    return null;
  }
};

/**
 * Set (or clear, passing mode=null) this cohort's submission-mode override
 * for a homework type
 */
export const setSubmissionModeOverride = async (cohortId, homeworkType, mode) => {
  try {
    await setDoc(
      doc(db, 'cohorts', cohortId, 'deadlines', 'current'),
      { [`${homeworkType}_modeOverride`]: mode },
      { merge: true }
    );
  } catch (error) {
    console.error('Set submission mode override error:', error);
    throw error;
  }
};

/**
 * Resolve the effective submission mode for a cohort + homework type:
 * per-cohort override if one is set, otherwise the global default, otherwise 'pdf'.
 */
export const getEffectiveSubmissionMode = async (cohortId, homeworkType) => {
  try {
    const override = await getSubmissionModeOverride(cohortId, homeworkType);
    if (override) return override;
    const modes = await getSubmissionModes();
    return modes[homeworkType] || 'pdf';
  } catch (error) {
    console.error('Get effective submission mode error:', error);
    return 'pdf';
  }
};

/**
 * Get a single cohort by its document ID
 */
export const getCohortById = async (cohortId) => {
  try {
    const snap = await getDoc(doc(db, 'cohorts', cohortId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error('Get cohort by id error:', error);
    return null;
  }
};

/**
 * Resolve which of the org's homework types are enabled for a cohort, with
 * an optional student-level override taking priority over it.
 *
 * The override (student.enabledHomeworkTypes, same field name/shape as the
 * cohort one, one level more specific) is written automatically by
 * ae-course-hosting's account-sync logic for a student enrolled there in
 * more than one course sharing this Homework Solutions org - see
 * mintHomeworkSolutionsToken/syncHomeworkSolutionsStudentAccess in that
 * repo's functions/index.js. Never tutor-set here; a student who only ever
 * registered directly on Homework Solutions simply never has this field,
 * and falls straight through to the existing cohort-level behavior below.
 *
 * Missing/undefined enabledHomeworkTypes (on either student or cohort)
 * means "all enabled" - this is the default so every cohort/student that
 * predates this feature is unaffected.
 */
export const resolveEnabledHomeworkTypes = (cohort, allTypes, student) => {
  const studentOverride = student?.enabledHomeworkTypes;
  if (Array.isArray(studentOverride) && studentOverride.length > 0) {
    return allTypes.filter(t => studentOverride.includes(t));
  }
  const enabled = cohort?.enabledHomeworkTypes;
  return Array.isArray(enabled) ? allTypes.filter(t => enabled.includes(t)) : allTypes;
};

/**
 * Set (or clear) which homework types are enabled for a cohort - the tasks
 * that cohort's students are actually offered and that show on the Deadlines page.
 */
export const setEnabledHomeworkTypes = async (cohortId, types) => {
  try {
    await updateCohort(cohortId, { enabledHomeworkTypes: types });
  } catch (error) {
    console.error('Set enabled homework types error:', error);
    throw error;
  }
};